import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// Renders a `<picture>` with AVIF primary + WebP fallback `<source>` elements
// and an `<img>` fallback, all derived from a base URL and a list of widths.
//
// Caller MUST pass `width` and `height` so the browser can reserve layout
// space (CLS = 0) and so Chrome's lazy-loading does not defer setting `src`.
//
// The AVIF source is listed first so browsers that support it (all in our
// browserslist as of Apr 2026) use the smaller file. Non-AVIF browsers fall
// through to WebP. The `<img>` src points to the smallest WebP variant as
// a last-resort fallback for bots / no-JS / SSR.
//
// A browser that picked the AVIF source does not move on to the WebP one when
// that file fails; it shows a broken image. So a failed AVIF drops the AVIF
// `<source>` and the browser re-selects the WebP. Cloudflare Bot Fight Mode on
// the Seattle and Portland zones blocks `.avif` (never `.webp`) for clients it
// scores as bots, e.g. headless Chrome; stock Chrome gets the AVIF (23 Sep 2026).
const isAvif = url => /\.avif$/.test(url);

function ResponsiveImg({ src, widths = [400, 600, 800, 1000, 1200, 1600, 2000], sizes, alt, width, height, onError, ...rest }) {
  const imgRef = useRef(null);
  const imgName = src.replace(/\.[^/.]+$/, '');
  // Keyed by image, so a new `src` on the same instance tries AVIF again.
  const [avifFailedFor, setAvifFailedFor] = useState(null);
  const avifSrcSet = widths.map(size => `${imgName}-${size}w.avif ${size}w`).join(', ');
  const webpSrcSet = widths.map(size => `${imgName}-${size}w.webp ${size}w`).join(', ');
  const defaultSrc = `${imgName}-${widths[0]}w.webp`;
  const resolvedSizes = sizes || '100vw';

  // An eager image can fail before hydration attaches `onError`.
  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth === 0 && isAvif(img.currentSrc)) setAvifFailedFor(imgName);
  }, [imgName]);

  const handleError = (event) => {
    if (isAvif(event.currentTarget.currentSrc)) setAvifFailedFor(imgName);
    if (onError) onError(event);
  };

  return (
    <picture>
      { avifFailedFor !== imgName &&
        <source type="image/avif" srcSet={avifSrcSet} sizes={resolvedSizes} />
      }
      <source type="image/webp" srcSet={webpSrcSet} sizes={resolvedSizes} />
      <img
        ref={imgRef}
        src={defaultSrc}
        alt={alt}
        width={width}
        height={height}
        onError={handleError}
        {...rest}
      />
    </picture>
  );
}

ResponsiveImg.propTypes = {
  src: PropTypes.string.isRequired,
  widths: PropTypes.arrayOf(PropTypes.number),
  sizes: PropTypes.string,
  alt: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onError: PropTypes.func,
};

export default ResponsiveImg;
